import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import { DeleteIcon } from 'lucide-react';
import React, { Dispatch, SetStateAction } from 'react';
import { VariantComment, VariantDetails, formatKey } from './types';
import { formatDateResults } from '@/utils/dateUtils';
import AcmgClassificationPanel from './AcmgClassificationPanel';

interface VariantDetailsPageProps {
  drawerOpen: boolean;
  handleCloseDrawer: () => void;
  drawerHeight: number;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  variantDetails: VariantDetails | undefined;
  selectedVariation: string | null;
  newComment: string;
  handleDeleteComment: (id: string) => Promise<void>;
  setNewComment: Dispatch<SetStateAction<string>>;
  allowValidateVariant: boolean;
  onValidateVariant: (id: string) => Promise<void>;
  onRejectVariant: (id: string) => Promise<void>;
  allowRejectVariant: boolean;
  onCommentVariant: (id: string) => Promise<void>;
  variantComments: VariantComment[];
}

const VariantDetailsPage = ({
  drawerOpen,
  handleCloseDrawer,
  drawerHeight,
  isDragging,
  handleMouseDown,
  selectedVariation,
  variantDetails,
  newComment,
  handleDeleteComment,
  setNewComment,
  allowValidateVariant,
  onValidateVariant,
  onRejectVariant,
  allowRejectVariant,
  onCommentVariant,
  variantComments,
}: VariantDetailsPageProps) => (
  <Drawer
    anchor="bottom"
    open={drawerOpen}
    onClose={handleCloseDrawer}
    PaperProps={{
      sx: {
        height: `${drawerHeight}vh`,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        transition: isDragging ? 'none' : 'height 0.2s ease-out',
      },
    }}
  >
    {/* Drag Handle */}
    <Box
      onMouseDown={handleMouseDown}
      sx={{
        height: 20,
        backgroundColor: 'background.default',
        cursor: 'ns-resize',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        '&:hover': {
          backgroundColor: 'action.hover',
        },
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          width: 40,
          height: 4,
          backgroundColor: 'grey.400',
          borderRadius: 2,
        }}
      />
    </Box>

    <Box sx={{ display: 'flex', height: 'calc(100% - 20px)' }}>
      {/* Left Panel: Variation Info */}
      <Box
        sx={{
          width: '50%',
          p: 3,
          overflowY: 'auto',
          borderRight: '1px solid',
          borderColor: 'divider',
        }}
      >
        {variantDetails && (
          <Box>
            {/* Header with action buttons */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Typography variant="h6" gutterBottom>
                Variant Details
              </Typography>

              {/** Validate/reject only for selected variants/validate results only */}
              {allowValidateVariant && (
                <Stack direction="row" spacing={1}>
                  {allowRejectVariant && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={async () => {
                        if (variantDetails) {
                          await onRejectVariant(
                            variantDetails.selected_variant_id,
                          );
                        } else {
                          enqueueSnackbar('Failed to retrieve variants ID', {
                            variant: 'error',
                          });
                        }
                      }}
                    >
                      Reject
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={async () => {
                      if (variantDetails) {
                        await onValidateVariant(
                          variantDetails.selected_variant_id,
                        );
                      } else {
                        enqueueSnackbar('Failed to retrieve variants ID', {
                          variant: 'error',
                        });
                      }
                    }}
                  >
                    Validate
                  </Button>
                </Stack>
              )}
            </Box>

            {/* Table of variant details */}
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableBody>
                  {Object.entries(variantDetails).map(([key, value]) => (
                    <TableRow key={key}>
                      <TableCell sx={{ fontWeight: 'bold', width: '40%' }}>
                        {formatKey(key)}
                      </TableCell>
                      <TableCell>
                        {typeof value === 'string' &&
                        value.match(/^https?:\/\//) ? (
                          <Button
                            variant="text"
                            color="primary"
                            href={value}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textTransform: 'none',
                              p: 0,
                              minWidth: 'unset',
                            }}
                          >
                            {value}
                          </Button>
                        ) : (
                          String(value)
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <AcmgClassificationPanel variantDetails={variantDetails} />
          </Box>
        )}
      </Box>

      {/* Right Panel: Comments */}
      <Box sx={{ width: '50%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ px: 3, py: 2 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              Comments for: <strong>{selectedVariation}</strong>
            </Typography>
            <Button onClick={handleCloseDrawer} size="small">
              Close
            </Button>
          </Box>
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
          <Typography variant="subtitle1" gutterBottom>
            Previous Comments
          </Typography>
          {Array.isArray(variantComments) && variantComments.length === 0 ? (
            <Typography color="text.secondary" textAlign="center" py={4}>
              No comments for this variant.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {Array.isArray(variantComments) &&
                variantComments.map((comment) => (
                  <Paper key={comment.id} variant="outlined" sx={{ p: 2 }}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box>
                        <Typography fontWeight="bold">
                          {comment.created_by_email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateResults(comment.created_at)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        aria-label="delete comment"
                        onClick={async () => {
                          await handleDeleteComment(comment.id);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                      {comment.comment}
                    </Typography>
                  </Paper>
                ))}
            </Stack>
          )}
        </Box>

        <Divider />

        {/* Add New Comment */}
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Write a comment..."
            variant="outlined"
            value={newComment}
            onChange={(e) => setNewComment(e.target.value)}
            autoFocus={drawerOpen}
          />
          <Button
            variant="contained"
            onClick={async () => {
              if (variantDetails) {
                await onCommentVariant(variantDetails.selected_variant_id);
              } else {
                enqueueSnackbar('Failed to retrieve variants ID', {
                  variant: 'error',
                });
              }
            }}
            disabled={!newComment.trim()}
            sx={{ alignSelf: 'flex-start', mt: 1 }}
          >
            Submit
          </Button>
        </Box>
      </Box>
    </Box>
  </Drawer>
);

export default VariantDetailsPage;
